import { Loader2, Minus, Plus, RotateCcw, Save, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ActionBar } from '@/components/common/ActionBar'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirmModal, Modal } from '@/components/ui/modal'
import { usePermissionCatalog } from '../api'
import { permissionLabel } from '../ui'
import { useAccessLookups } from '../useLookups'
import type { Role } from '../types'

type BarProps = {
  added: string[]
  removed: string[]
  saving: boolean
  onSave: () => void
  onDiscard: () => void
  onReview?: () => void
}

export function ChangeSummaryBar({ added, removed, saving, onSave, onDiscard, onReview }: BarProps) {
  const { t } = useTranslation()

  const discard = async () => {
    const ok = await confirmModal({
      title: t('access.detail.discardTitle'),
      description: t('access.detail.discardBody', { count: added.length + removed.length }),
      confirmLabel: t('access.detail.discard'),
      destructive: true,
    })
    if (ok) onDiscard()
  }

  return (
    <ActionBar
      title={t('access.detail.changeSummary')}
      hint={t('access.detail.unsaved')}
      meta={
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="flex items-center gap-1.5 text-sm">
            <span className="flex size-5 items-center justify-center rounded-full bg-success/15 text-success">
              <Plus className="size-3" />
            </span>
            <span className="font-semibold text-success">+{added.length}</span>
            <span className="text-muted-foreground">{t('access.detail.addedShort')}</span>
          </span>
          <span className="flex items-center gap-1.5 text-sm">
            <span className="flex size-5 items-center justify-center rounded-full bg-destructive/12 text-destructive">
              <Minus className="size-3" />
            </span>
            <span className="font-semibold text-destructive">-{removed.length}</span>
            <span className="text-muted-foreground">{t('access.detail.removedShort')}</span>
          </span>
        </div>
      }
    >
      <Button size="sm" variant="ghost" disabled={saving} onClick={() => void discard()}>
        <RotateCcw />
        {t('access.detail.discard')}
      </Button>
      {onReview ? (
        <Button size="sm" variant="outline" disabled={saving} onClick={onReview}>
          {t('access.detail.reviewAction')}
        </Button>
      ) : null}
      <Button size="sm" disabled={saving} onClick={onSave}>
        {saving ? <Loader2 className="animate-spin" /> : <Save />}
        {t('access.detail.save')}
      </Button>
    </ActionBar>
  )
}

type ReviewProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  added: string[]
  removed: string[]
  role: Role
  saving: boolean
  onConfirm: () => void
}

export function SaveChangesModal({ open, onOpenChange, added, removed, role, saving, onConfirm }: ReviewProps) {
  const { t } = useTranslation()
  const catalog = usePermissionCatalog().data
  const { userById } = useAccessLookups()
  const affected = role.assignments
    .map((a) => userById.get(a.userId))
    .filter(Boolean)
    .slice(0, 8)

  const rows = [
    ...added.map((code) => ({ code, kind: 'add' as const })),
    ...removed.map((code) => ({ code, kind: 'remove' as const })),
  ]

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      dismissible={!saving}
      icon={<Save />}
      title={t('access.detail.reviewTitle')}
      description={t('access.detail.reviewHint', { count: role.assignments.length })}
      footer={
        <>
          <Button variant="cancel" disabled={saving} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button disabled={saving} onClick={onConfirm}>
            {saving ? <Loader2 className="animate-spin" /> : <Save />}
            {t('access.detail.confirmSave')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <SummaryStat label={t('access.detail.stats.changed')} value={String(added.length + removed.length)} />
          <SummaryStat label={t('access.detail.stats.affected')} value={String(role.assignments.length)} />
          <SummaryStat label={t('access.detail.stats.added')} value={`+${added.length}`} tone="success" />
          <SummaryStat label={t('access.detail.stats.removed')} value={`-${removed.length}`} tone="danger" />
        </div>

        <div className="overflow-hidden rounded-xl border">
          <div className="grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem] gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
            <span>{t('access.detail.review.columns.permission')}</span>
            <span className="text-center">{t('access.detail.review.columns.before')}</span>
            <span className="text-center">{t('access.detail.review.columns.after')}</span>
          </div>
          <ul className="max-h-64 divide-y overflow-y-auto">
            {rows.map((row) => (
              <li key={`${row.kind}-${row.code}`} className="grid grid-cols-[minmax(0,1fr)_5.5rem_5.5rem] items-center gap-2 px-3 py-2 text-sm">
                <span className="truncate">{permissionLabel(t, row.code, catalog ?? [])}</span>
                <span className="flex justify-center">
                  <Badge tone={row.kind === 'add' ? 'neutral' : 'success'}>{row.kind === 'add' ? t('access.detail.review.absent') : t('access.detail.review.present')}</Badge>
                </span>
                <span className="flex justify-center">
                  <Badge tone={row.kind === 'add' ? 'success' : 'danger'}>{row.kind === 'add' ? t('access.detail.review.present') : t('access.detail.review.absent')}</Badge>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Users className="size-3.5" />
            {t('access.detail.affectedUsers')}
          </p>
          {affected.length ? (
            <div className="flex flex-wrap items-center gap-2">
              {affected.map((u) =>
                u ? (
                  <div key={u.id} className="flex items-center gap-1.5 rounded-full border bg-card px-2 py-1">
                    <Avatar name={u.name} className="size-5 [&>span]:text-[9px]" />
                    <span className="text-xs">{u.name}</span>
                  </div>
                ) : null,
              )}
              {role.assignments.length > affected.length ? (
                <span className="text-xs text-muted-foreground">{t('access.moreCount', { count: role.assignments.length - affected.length })}</span>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('access.users.empty')}</p>
          )}
        </div>
      </div>
    </Modal>
  )
}

function SummaryStat({ label, value, tone }: { label: string; value: string; tone?: 'success' | 'danger' }) {
  return (
    <div className="rounded-xl border px-3 py-2">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={tone === 'success' ? 'text-lg font-semibold text-success' : tone === 'danger' ? 'text-lg font-semibold text-destructive' : 'text-lg font-semibold'}>{value}</p>
    </div>
  )
}
